'use client';

import Link from '@eliadmin/core/src/components/link';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { sendContactMessage } from '@eliadmin/core/src/api/forms/forms-api';

import { Button } from '@eliadmin/core/src/components/ui/button';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@eliadmin/core/src/components/ui/form';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { Textarea } from '@eliadmin/core/src/components/ui/textarea';
import { toast } from '@eliadmin/core/src/components/ui/use-toast';
import { CONTACT_CONFIG } from '@eliadmin/core/src/constants/contact-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getDefaultUrlForTosOrGdprLocalized } from '@eliadmin/core/src/utils/misc-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { FiSend } from 'react-icons/fi';
import { z } from 'zod';

import { buildBuyoutLeadMessage, isValidApproximateArea } from './buyout-lead-payload';

const BUYOUT_SUBJECT = 'Odkúpenie nehnuteľnosti';

const buyoutLeadSchema = z.object({
  name: z.string().trim().min(1, 'Zadajte meno.'),
  surname: z.string().trim().min(1, 'Zadajte priezvisko.'),
  email: z.string().trim().email('Zadajte platnú e-mailovú adresu.'),
  phone: z.string().trim().min(1, 'Zadajte telefónne číslo.'),
  propertyType: z.string().trim().min(1, 'Zadajte typ nehnuteľnosti.'),
  location: z.string().trim().min(1, 'Zadajte lokalitu nehnuteľnosti.'),
  area: z.string().trim().refine(isValidApproximateArea, {
    message: 'Výmeru zadajte ako číslo v m².',
  }),
  text: z.string().trim().min(1, 'Doplňte základné informácie o nehnuteľnosti.'),
  gdpr: z.boolean().refine(value => value, {
    message: 'Na odoslanie formulára je potrebný súhlas so spracovaním osobných údajov.',
  }),
  'g-recaptcha-response': z.string().default(''),
});

type BuyoutLeadInput = z.input<typeof buyoutLeadSchema>;
type BuyoutLeadValues = z.output<typeof buyoutLeadSchema>;

const FORM_FIELD_NAMES = new Set<keyof BuyoutLeadInput>([
  'name',
  'surname',
  'email',
  'phone',
  'propertyType',
  'location',
  'area',
  'text',
  'gdpr',
  'g-recaptcha-response',
]);

export function BuyoutLeadForm() {
  const locale = useLocaleStore(state => state.currentLocale);

  const [status, setStatus] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);

  const form = useForm<BuyoutLeadInput, undefined, BuyoutLeadValues>({
    resolver: zodResolver(buyoutLeadSchema),
    defaultValues: {
      name: '',
      surname: '',
      email: '',
      phone: '',
      propertyType: '',
      location: '',
      area: '',
      text: '',
      gdpr: false,
      'g-recaptcha-response': '',
    },
  });

  const { mutateAsync, isPending } = useMutation({ mutationFn: sendContactMessage });
  const isCaptchaReady = typeof executeRecaptcha === 'function';
  const isSubmitting = form.formState.isSubmitting || isPending;

  const handleSubmit = form.handleSubmit(async data => {
    setStatus(null);

    try {
      const captchaToken = '';

      if (!captchaToken.trim()) {
        throw new Error('Bezpečnostné overenie formulára nevrátilo platný token.');
      }

      const sourcedMessage = buildBuyoutLeadMessage(data);

      await mutateAsync({
        locale,
        data: {
          name: data.name,
          surname: data.surname,
          email: data.email,
          phone: data.phone,
          subject: BUYOUT_SUBJECT,
          text: sourcedMessage,
          gdpr: data.gdpr,

        },
      });

      const message = 'Ďakujeme. Správu sme odoslali a ozveme sa vám.';
      setStatus({ kind: 'success', message });
      toast({ title: message, variant: 'success' });
      form.reset();
    } catch (error) {
      if (error instanceof ValidationError) {
        for (const [field, messages] of Object.entries(error.fields)) {
          if (!FORM_FIELD_NAMES.has(field as keyof BuyoutLeadInput)) continue;

          form.setError(field as keyof BuyoutLeadInput, {
            type: 'server',
            message: messages.join('\n'),
          });
        }
      }

      const message = 'Správu sa nepodarilo odoslať. Skontrolujte údaje a skúste to znova.';
      setStatus({ kind: 'error', message });
      toast({
        title: 'Odoslanie sa nepodarilo',
        description: error instanceof Error ? error.message : message,
        variant: 'destructive',
      });
    }
  });

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        className="grid w-full grid-cols-12 gap-4"
        noValidate
        aria-label="Kontaktný formulár k odkúpeniu nehnuteľnosti"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem required className="col-span-12 sm:col-span-6">
              <FormLabel>Meno</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  autoComplete="given-name"
                  placeholder="Meno *"
                  variant="outlined"
                  rounded={CONTACT_CONFIG.form.input.rounded}
                  aria-required="true"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="surname"
          render={({ field }) => (
            <FormItem required className="col-span-12 sm:col-span-6">
              <FormLabel>Priezvisko</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  autoComplete="family-name"
                  placeholder="Priezvisko *"
                  variant="outlined"
                  rounded={CONTACT_CONFIG.form.input.rounded}
                  aria-required="true"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem required className="col-span-12 sm:col-span-6">
              <FormLabel>E-mail</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="E-mail *"
                  variant="outlined"
                  rounded={CONTACT_CONFIG.form.input.rounded}
                  aria-required="true"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem required className="col-span-12 sm:col-span-6">
              <FormLabel>Telefón</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder="Telefón *"
                  variant="outlined"
                  rounded={CONTACT_CONFIG.form.input.rounded}
                  aria-required="true"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="col-span-12 mt-2 border-t border-border pt-6">
          <h3 className="text-lg font-bold text-foreground">Základné údaje o nehnuteľnosti</h3>
          <p className="mt-1 text-sm leading-6 text-muted-foreground">
            Stačí orientačný údaj. Podrobnosti si môžeme doplniť pri ďalšom kontakte.
          </p>
        </div>

        <FormField
          control={form.control}
          name="propertyType"
          render={({ field }) => (
            <FormItem required className="col-span-12 sm:col-span-6">
              <FormLabel>Typ nehnuteľnosti</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="Byt, dom, pozemok… *"
                  variant="outlined"
                  rounded={CONTACT_CONFIG.form.input.rounded}
                  aria-required="true"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem required className="col-span-12 sm:col-span-6">
              <FormLabel>Lokalita</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  autoComplete="address-level2"
                  placeholder="Mesto alebo obec *"
                  variant="outlined"
                  rounded={CONTACT_CONFIG.form.input.rounded}
                  aria-required="true"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="area"
          render={({ field }) => (
            <FormItem className="col-span-12 sm:col-span-6">
              <FormLabel>Približná výmera v m² (voliteľné)</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  type="text"
                  inputMode="decimal"
                  placeholder="Napr. 72"
                  variant="outlined"
                  rounded={CONTACT_CONFIG.form.input.rounded}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="text"
          render={({ field }) => (
            <FormItem required className="col-span-12">
              <FormLabel>Doplňujúce informácie</FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  rows={6}
                  placeholder="Stav, očakávaný termín predaja a ďalšie dôležité informácie *"
                  variant="outlined"
                  rounded={CONTACT_CONFIG.form.input.rounded}
                  aria-required="true"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="gdpr"
          render={({ field }) => (
            <FormItem className="col-span-12 flex items-start gap-3">
              <FormControl>
                <Checkbox
                  ref={field.ref}
                  name={field.name}
                  disabled={field.disabled}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  aria-required="true"
                  className="mt-0.5"
                />
              </FormControl>
              <div className="space-y-1">
                <FormLabel className="text-sm leading-5">
                  Súhlasím so spracovaním osobných údajov podľa{' '}
                  <Link
                    href={getDefaultUrlForTosOrGdprLocalized(
                      ROUTES?.GDPR?.() ?? '/',
                      locale,
                      'gdpr',
                    )}
                    prefetch={false}
                    className="font-semibold text-primary underline underline-offset-2"
                  >
                    zásad ochrany osobných údajov
                  </Link>
                  .
                </FormLabel>
                <FormMessage />
              </div>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="g-recaptcha-response"
          render={() => (
            <FormItem className="col-span-12">
              <FormMessage />
            </FormItem>
          )}
        />

        {!isCaptchaReady && (
          <p
            id="buyout-captcha-status"
            className="col-span-12 text-sm leading-6 text-muted-foreground"
            role="status"
            aria-live="polite"
          >
            Načítava sa zabezpečenie formulára. Odoslanie sa o chvíľu aktivuje.
          </p>
        )}

        {status && (
          <p
            className={cn(
              'col-span-12 rounded-lg px-4 py-3 text-sm font-medium',
              status.kind === 'success'
                ? 'bg-success/10 text-success'
                : 'bg-destructive/10 text-destructive',
            )}
            role={status.kind === 'error' ? 'alert' : 'status'}
            aria-live="polite"
          >
            {status.message}
          </p>
        )}

        <Button
          type="submit"
          variant={CONTACT_CONFIG.form.submit.variant}
          className="col-span-12 mt-2 min-h-12 sm:col-span-6"
          isLoading={isSubmitting}
          disabled={isSubmitting || !isCaptchaReady}
          aria-describedby={!isCaptchaReady ? 'buyout-captcha-status' : undefined}
        >
          <FiSend className="mr-2 h-5 w-5" aria-hidden="true" />
          Poslať informácie
        </Button>
      </form>
    </Form>
  );
}
